import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getCsrfToken } from './csrf';

const SAFE_METHODS = new Set(['get', 'head', 'options']);

/**
 * Setup global axios interceptors for handling authentication errors
 * This will automatically logout users when their JWT token expires (401)
 */
export const setupAxiosInterceptors = (logoutCallback: () => void) => {
  // Request interceptor — session auth now rides on the httpOnly cookie automatically
  // (see axios.defaults.withCredentials in index.tsx); this only needs to add the
  // CSRF header for state-changing requests.
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const method = (config.method || 'get').toLowerCase();
      if (!SAFE_METHODS.has(method) && config.headers) {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle auth errors
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      // Check if error is 401 Unauthorized (token expired or invalid)
      // Never react to a failed logout call itself — logout() fires that request, so doing
      // so loops forever once the session is already dead.
      if (error.response?.status === 401 && !error.config?.url?.includes('/auth/logout')) {
        const errorMessage = error.response?.data as any;

        // Check if it's specifically a token expiration error
        if (
          errorMessage?.error?.includes('expired') ||
          errorMessage?.error?.includes('invalid token') ||
          errorMessage?.error?.includes('jwt expired')
        ) {
          console.warn('🔒 Token expired, logging out user...');

          // The cookie itself is httpOnly (can't be cleared from JS) and will have
          // already been rejected server-side — this just clears the local UI cache.
          localStorage.removeItem('user');

          // Call logout callback to update auth context
          logoutCallback();

          // Redirect to login page
          if (window.location.pathname !== '/login' && window.location.pathname !== '/auth') {
            window.location.href = '/login?expired=true';
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Clear all axios interceptors (useful for cleanup)
 */
export const clearAxiosInterceptors = () => {
  axios.interceptors.request.clear();
  axios.interceptors.response.clear();
};
